/**
 * Cookie-based storage for Supabase auth.
 * Enables shared session across app.golfauth.com and fitting.golfauth.com
 * (and the apex golfauth.com SPA) in **production**.
 *
 * Staging (`staging.golfauth.com`) must NOT share Domain=.golfauth.com
 * cookies with production — that leaked prod JWTs into the staging SPA
 * (and vice versa), so App Store review logins on prod could appear
 * "logged in" on staging while club writes hit the staging Supabase
 * project. Staging uses host-only cookies + a distinct storage key.
 *
 * Cookie names are sanitized (replace dots with underscores).
 */

/** Max encoded cookie value length — browsers often cap ~4096 bytes total
 *  per cookie (name + value + attributes). Leave headroom for the `ga_`
 *  name and attributes. Check ENCODED length; raw UTF-8 under 3800 can
 *  still overflow after encodeURIComponent. */
export const MAX_AUTH_COOKIE_ENCODED_CHARS = 3500;

/** Production Supabase auth storage key (cookie name prefix `ga_…`). */
export const PROD_AUTH_STORAGE_KEY = 'sb-auth-token';

/**
 * Staging-only storage key so prod Domain=.golfauth.com cookies are never
 * read by the staging client (different cookie / localStorage name).
 */
export const STAGING_AUTH_STORAGE_KEY = 'sb-auth-token-staging';

/**
 * True for the staging apex / API hosts. Keep this list tight — do not
 * treat every `*.golfauth.com` host as staging.
 */
export function isGolfAuthStagingHost(hostname: string): boolean {
  const host = (hostname ?? '').trim().toLowerCase();
  return (
    host === 'staging.golfauth.com' ||
    host === 'staging-app.golfauth.com' ||
    host.endsWith('.staging.golfauth.com')
  );
}

/**
 * Supabase `auth.storageKey` for this hostname. Staging must differ from
 * production so shared-domain cookies cannot cross environments.
 */
export function resolveGolfAuthAuthStorageKey(hostname: string): string {
  return isGolfAuthStagingHost(hostname)
    ? STAGING_AUTH_STORAGE_KEY
    : PROD_AUTH_STORAGE_KEY;
}

/**
 * Resolve the Domain= attribute for GolfAuth production hosts.
 * Apex `golfauth.com` must use `.golfauth.com` (same as subdomains) so
 * Fitting at the apex and Core at app.golfauth.com share one session.
 * Host-only cookies on the apex previously competed with Domain=
 * cookies from subdomains and caused post-OTP REST 401s (anon JWT).
 *
 * Staging returns `undefined` (host-only) — never Domain=.golfauth.com.
 */
export function resolveGolfAuthCookieDomain(hostname: string): string | undefined {
  const host = (hostname ?? '').trim().toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1') return undefined;
  if (isGolfAuthStagingHost(host)) return undefined;
  if (host === 'golfauth.com' || host.endsWith('.golfauth.com')) {
    return '.golfauth.com';
  }
  return undefined;
}

/** Prefer cookie when the encoded payload fits; otherwise localStorage. */
export function shouldStoreSessionInCookie(
  rawValue: string,
  maxEncoded: number = MAX_AUTH_COOKIE_ENCODED_CHARS,
): boolean {
  return encodeURIComponent(rawValue).length <= maxEncoded;
}

function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return resolveGolfAuthCookieDomain(window.location.hostname);
}

function toCookieName(key: string): string {
  return 'ga_' + key.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function getCookie(name: string): string | null {
  const cookieName = toCookieName(name);
  const match = document.cookie.match(
    new RegExp('(^| )' + cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]+)'),
  );
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string, domain?: string): void {
  const cookieName = toCookieName(name);
  const domainAttr = domain ? `; domain=${domain}` : '';
  const maxAge = 60 * 60 * 24 * 7; // 7 days
  document.cookie = `${cookieName}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax; secure${domainAttr}`;
}

function removeCookie(name: string, domain?: string): void {
  const cookieName = toCookieName(name);
  const domainAttr = domain ? `; domain=${domain}` : '';
  document.cookie = `${cookieName}=; path=/; max-age=0${domainAttr}`;
}

/** Clear both host-only and Domain=.golfauth.com copies to avoid stale duplicates. */
function removeAuthCookies(key: string, domain?: string): void {
  removeCookie(key, undefined);
  if (domain) removeCookie(key, domain);
}

export function createCookieStorage(): {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
} {
  const domain = getCookieDomain();

  return {
    getItem(key: string): string | null {
      // Cookie is the canonical store (it crosses .golfauth.com subdomains
      // so SSO between Core and Fitting works). But setItem falls back to
      // localStorage when the session exceeds the cookie size limit —
      // so getItem MUST mirror that fallback or the JWT vanishes on read.
      // Without this, supabase-js sees no session, the next REST request
      // goes out as anon, and column-level RLS (migration 20260508103810
      // on `professionals`) returns 401 — surfacing as "No golfer profile
      // found" right after OTP verify succeeds.
      const fromCookie = getCookie(key);
      if (fromCookie !== null && fromCookie.length > 0) return fromCookie;
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key: string, value: string): void {
      if (!shouldStoreSessionInCookie(value)) {
        console.warn('[GolfAuth] Session too large for cookie; using localStorage for this origin');
        try {
          localStorage.setItem(key, value);
        } catch {
          // ignore
        }
        removeAuthCookies(key, domain);
        return;
      }
      // Drop host-only + Domain= copies first so we never leave two
      // same-name cookies that document.cookie resolves nondeterministically.
      removeAuthCookies(key, domain);
      setCookie(key, value, domain);
      // Mirror into localStorage as a same-origin backup. getItem still
      // prefers the cookie (needed for cross-subdomain SSO), but if the
      // browser drops/truncates the cookie the JWT remains readable here
      // so post-OTP profile fetches don't fall back to anon → 401.
      try {
        localStorage.setItem(key, value);
      } catch {
        // ignore
      }
    },
    removeItem(key: string): void {
      removeAuthCookies(key, domain);
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}
